import type {MouseEvent, ReactElement} from 'react';
import React, {useState} from 'react';
import {ClickableDiv} from '@Components/clickable-div';
import {Text, TextSize, TextType} from '@Components/text';
import type {VoiceProfilePillProps} from './voice-profile-pill.types';
import styles from './voice-profile-pill.module.scss';
import {useAudioPlayback} from '@Hooks/ai-text-to-speech/useAudioPlayback';
import {PlayPauseButton} from '@Panels/ai-voice-panel/components/play-pause-button/play-pause-button';

const PILL_PLAY_PAUSE_DIAMETER = 28;

export function VoiceProfilePill(props: VoiceProfilePillProps): ReactElement<HTMLDivElement> {
  const {avatarUrl, name, soundtrack, isSelected = false} = props;
  const [isHovered, setIsHovered] = useState(false);
  const audioPlayback = useAudioPlayback();

  const handlePlayPauseClick = (e: MouseEvent): void => {
    e.stopPropagation();
    e.preventDefault();
    audioPlayback.togglePlayPause(soundtrack);
  };

  const showPlayPauseIcon = isHovered || audioPlayback.isPlaying;

  return (
    <ClickableDiv
      className={`flexbox flex-align-items-center spacing-p-l-10 spacing-p-r-3 radius-round _position-relative _cursor-pointer hover-transition-all ${styles.pill} ${isSelected ? styles.pillSelected : ''} ${audioPlayback.isPlaying ? styles.pillPlaying : ''}`}
      onClick={() => {
        props.onClick();
      }}
      onMouseEnter={() => {
        setIsHovered(true);
      }}
      onMouseLeave={() => {
        setIsHovered(false);
      }}
    >
      <div className={`_position-absolute ${styles.avatarContainer}`}>
        <img className={`${styles.avatar} ${showPlayPauseIcon ? '_hidden' : ''}`} src={avatarUrl} alt={name} loading="lazy" />
      </div>
      {showPlayPauseIcon ? (
        <PlayPauseButton
          isPlaying={audioPlayback.isPlaying}
          playbackProgress={audioPlayback.playbackProgress}
          onClick={handlePlayPauseClick}
          diameter={PILL_PLAY_PAUSE_DIAMETER}
          iconClassName={isSelected ? 'content-primary' : ''}
          className={`_position-absolute ${styles.playPauseOverlay}`}
        />
      ) : null}
      <Text className={`${isSelected ? 'content-primary' : ''} ${styles.pillName}`} disableSelection bold size={TextSize.XXSMALL} type={TextType.BODY} val={name} />
    </ClickableDiv>
  );
}
